import { useMemo } from 'react';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type InvalidateQueryFilters,
    type QueryFilters,
    type QueryKey,
    type RefetchQueryFilters,
    type UseMutationOptions,
    type UseMutationResult,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';

/*
 * Per-user TanStack Query key scoping — defense in depth for client-side
 * PHI cache hygiene (GSAPM-399; HIPAA backend plan §1.7a).
 *
 * Why: `queryClient.clear()` already fires on every setSession / logout,
 * which covers the known cross-user exposure path (regression test in
 * stores/auth.test.ts). But that is imperative defense — a new logout
 * entrypoint added later, or a hard navigation that skips the auth store,
 * would re-expose cached PHI from the previous user.
 *
 * The wrappers in this module prepend the current Cognito `sub` (or a
 * stable `__anon__` sentinel for pre-login traffic) to every queryKey,
 * so a query observer registered under user A's session can NEVER read
 * cache data registered under user B's session — the cache lookup keys
 * are structurally different. The same logic applies to every
 * QueryClient method that takes a queryKey: invalidate, refetch,
 * getQueryData, setQueryData, getQueriesData, etc.
 *
 * Call-site migration is mechanical: imports of useQuery / useMutation /
 * useQueryClient from '@tanstack/react-query' move to this module's
 * useScopedQuery / useScopedMutation / useScopedQueryClient. The
 * `queryKey: [...]` written at the call site is the TAIL — the wrapper
 * prepends the userId. Existing code that does `qc.invalidateQueries({
 * queryKey: ['behavior-types'] })` keeps that exact shape; the wrapper
 * scopes the key transparently.
 *
 * The ESLint rule under apps/pma-client/eslint.config.js forbids importing
 * useQuery / useMutation / useQueryClient directly from
 * '@tanstack/react-query' inside `src/api/` and `src/features/` — every
 * new query / mutation MUST go through this module.
 */

// Reserved when no user is logged in. Pre-auth traffic (the IdP callback
// handshake) never touches PHI, so anonymous cache entries can't leak across
// sessions — but we still tag them distinctly so they can't be mistaken
// for an authenticated user's entries.
export const ANON_SCOPE = '__anon__';

/** Read the current scope token. Reactive — re-renders on user change. */
function useUserScope(): string {
    return useAuthStore((s) => s.user?.id) ?? ANON_SCOPE;
}

function prefix(userId: string, queryKey: QueryKey | undefined): QueryKey {
    return queryKey ? [userId, ...queryKey] : [userId];
}

function scopeFilters<F extends { queryKey?: QueryKey }>(
    userId: string,
    filters: F | undefined,
): F | undefined {
    if (!filters) return filters;
    // queryKey is optional on QueryFilters — only rewrite it when set.
    if (filters.queryKey === undefined) return filters;
    return { ...filters, queryKey: prefix(userId, filters.queryKey) };
}

/**
 * useScopedQuery — drop-in replacement for useQuery that prepends the
 * current user id (or ANON_SCOPE) to the queryKey.
 *
 * The call site still writes the "logical" queryKey:
 *
 *   useScopedQuery({ queryKey: ['behavior-types'] as const, queryFn })
 *
 * Internally the observer subscribes to `[userId, 'behavior-types']`.
 * Logout / login changes userId, the observer re-subscribes to the new
 * scoped key, and the previous user's cache entry is unreachable from
 * the new user's session.
 */
export function useScopedQuery<
    TQueryFnData = unknown,
    TError = Error,
    TData = TQueryFnData,
>(
    options: UseQueryOptions<TQueryFnData, TError, TData>,
): UseQueryResult<TData, TError> {
    const userId = useUserScope();
    return useQuery({
        ...options,
        queryKey: prefix(userId, options.queryKey),
    } as UseQueryOptions<TQueryFnData, TError, TData>);
}

/**
 * useScopedMutation — passes through to useMutation. Mutations do not
 * have a cache key; this re-export exists so api/ modules can import
 * everything they need from one place and the ESLint rule has a
 * uniform target.
 */
export function useScopedMutation<
    TData = unknown,
    TError = Error,
    TVariables = void,
    TContext = unknown,
>(
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
    return useMutation(options);
}

/**
 * Shim returned by useScopedQueryClient — exposes every QueryClient
 * method that takes a queryKey, automatically prefixing the userId.
 * Same call signatures as the underlying methods, so existing
 * `qc.invalidateQueries({ queryKey: [...] })` sites keep their exact
 * shape — only the import changes.
 */
export interface ScopedQueryClient {
    invalidateQueries: (filters?: InvalidateQueryFilters) => Promise<void>;
    refetchQueries: (filters?: RefetchQueryFilters) => Promise<void>;
    removeQueries: (filters?: QueryFilters) => void;
    cancelQueries: (filters?: QueryFilters) => Promise<void>;
    resetQueries: (filters?: QueryFilters) => Promise<void>;
    getQueryData: <TData = unknown>(queryKey: QueryKey) => TData | undefined;
    setQueryData: <TData = unknown>(
        queryKey: QueryKey,
        updater:
            | TData
            | undefined
            | ((oldData: TData | undefined) => TData | undefined),
    ) => TData | undefined;
    getQueriesData: <TData = unknown>(
        filters: QueryFilters,
    ) => Array<[QueryKey, TData | undefined]>;
    setQueriesData: <TData = unknown>(
        filters: QueryFilters,
        updater:
            | TData
            | undefined
            | ((oldData: TData | undefined) => TData | undefined),
    ) => Array<[QueryKey, TData | undefined]>;
}

export function useScopedQueryClient(): ScopedQueryClient {
    const qc = useQueryClient();
    const userId = useUserScope();
    return useMemo<ScopedQueryClient>(
        () => ({
            invalidateQueries: (filters) =>
                qc.invalidateQueries(scopeFilters(userId, filters)),
            refetchQueries: (filters) =>
                qc.refetchQueries(scopeFilters(userId, filters)),
            removeQueries: (filters) =>
                qc.removeQueries(scopeFilters(userId, filters)),
            cancelQueries: (filters) =>
                qc.cancelQueries(scopeFilters(userId, filters)),
            resetQueries: (filters) =>
                qc.resetQueries(scopeFilters(userId, filters)),
            getQueryData: <T,>(queryKey: QueryKey) =>
                qc.getQueryData<T>(prefix(userId, queryKey)),
            setQueryData: <T,>(
                queryKey: QueryKey,
                updater:
                    | T
                    | undefined
                    | ((oldData: T | undefined) => T | undefined),
            ) =>
                qc.setQueryData<T>(
                    prefix(userId, queryKey),
                    updater as Parameters<typeof qc.setQueryData<T>>[1],
                ),
            getQueriesData: <T,>(filters: QueryFilters) =>
                qc.getQueriesData<T>(scopeFilters(userId, filters) ?? {}) as Array<
                    [QueryKey, T | undefined]
                >,
            setQueriesData: <T,>(
                filters: QueryFilters,
                updater:
                    | T
                    | undefined
                    | ((oldData: T | undefined) => T | undefined),
            ) =>
                qc.setQueriesData<T>(
                    scopeFilters(userId, filters) ?? {},
                    updater as Parameters<typeof qc.setQueriesData<T>>[1],
                ) as Array<[QueryKey, T | undefined]>,
        }),
        [qc, userId],
    );
}
